import { Injectable } from '@angular/core';
import { ToastrService, IndividualConfig, ActiveToast } from 'ngx-toastr';

export enum ToastType {
    SUCCESS = 'success',
    ERROR = 'error',
    INFO = 'info',
    WARNING = 'warning',
    PRIMARY = 'primary'
}

export interface ToastAction {
    text: string;
    onClick: () => void;
    cssClass?: string;
}

@Injectable({
    providedIn: 'root'
})
export class CustomToastService {
    private readonly DEFAULT_TIMEOUT = 90000;
    private readonly MOBILE_MAX_WIDTH = 430;
    private readonly MOBILE_TIMEOUT = 5000;

    constructor(private toastr: ToastrService) { }

    private isMobileViewport(): boolean {
        return typeof window !== 'undefined' &&
            window.matchMedia(`(max-width: ${this.MOBILE_MAX_WIDTH}px)`).matches;
    }

    private mergeToastOptions(options?: Partial<IndividualConfig>): Partial<IndividualConfig> {
        const isMobile = this.isMobileViewport();
        const defaults: Partial<IndividualConfig> = {
            timeOut: isMobile ? this.MOBILE_TIMEOUT : this.DEFAULT_TIMEOUT,
        };

        if (isMobile) {
            defaults.positionClass = 'toast-top-center';
        }

        return { ...defaults, ...options };
    }

    // Base method to show toast with custom options
    showToast(message: string, title: string, type: ToastType, options?: any): ActiveToast<any> {
        const toastOptions = this.mergeToastOptions(options);
        let toast: ActiveToast<any>;

        switch (type) {
            case ToastType.SUCCESS:
                toast = this.toastr.success(message, title, toastOptions);
                break;
            case ToastType.ERROR:
                toast = this.toastr.error(message, title, toastOptions);
                break;
            case ToastType.INFO:
                toast = this.toastr.info(message, title, toastOptions);
                break;
            case ToastType.WARNING:
                toast = this.toastr.warning(message, title, toastOptions);
                break;
            case ToastType.PRIMARY:
                toast = this.toastr.show(message, title, {
                    ...toastOptions,
                    toastClass: 'ngx-toastr toast-primary'
                });
                break;
            default:
                toast = this.toastr.info(message, title, toastOptions);
        }

        return toast;
    }

    // Predefined toast types with default styling
    showSuccess(message: string, title: string = 'Success', options?: any): ActiveToast<any> {
        return this.showToast(message, title, ToastType.SUCCESS, options);
    }

    showError(message: string, title: string = 'Error', options?: any): ActiveToast<any> {
        return this.showToast(message, title, ToastType.ERROR, options);
    }

    showInfo(message: string, title: string = 'Info', options?: any): ActiveToast<any> {
        return this.showToast(message, title, ToastType.INFO, options);
    }

    showWarning(message: string, title: string = 'Warning', options?: any): ActiveToast<any> {
        return this.showToast(message, title, ToastType.WARNING, options);
    }

    // Custom styled toasts
    showPrimaryToast(message: string, title: string = 'Notice', options?: any): ActiveToast<any> {
        return this.showToast(message, title, ToastType.PRIMARY, {
            enableHtml: true,
            closeButton: true,
            ...options
        });
    }

    showDarkToast(message: string, title: string = 'System', options?: any): ActiveToast<any> {
        return this.showToast(message, title, ToastType.INFO, {
            toastClass: 'ngx-toastr toast-dark',
            progressBar: true,
            ...options
        });
    }

    showCustomToast(message: string, title: string, options: any): ActiveToast<any> {
        return this.toastr.show(message, title, this.mergeToastOptions(options));
    }

    // New method with action button positioned according to design
    showToastWithAction(
        message: string,
        title: string,
        type: ToastType,
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        // Create custom layout for the toast with action button
        const customHtml = `
            <div class="toast-wrapper-action">
                <div class="toast-icon-container-action"></div>
                <div class="toast-action-container-action">
                    <button class="${action.cssClass}">${action.text}</button>
                </div>
                <div class="toast-content-action">
                    <div class="toast-title-action">${title}</div>
                    <div class="toast-message-action">${message}</div>
                </div>
            </div>
        `;

        // Define the toast class based on type
        let toastClass;
        switch (type) {
            case ToastType.SUCCESS:
                toastClass = 'ngx-toastr toast-success';
                break;
            case ToastType.ERROR:
                toastClass = 'ngx-toastr toast-error';
                break;
            case ToastType.WARNING:
                toastClass = 'ngx-toastr toast-warning';
                break;
            case ToastType.PRIMARY:
                toastClass = 'ngx-toastr toast-primary';
                break;
            default:
                toastClass = 'ngx-toastr toast-info';
        }

        const toastOptions = {
            enableHtml: true,
            closeButton: true,
            toastClass: toastClass,
            ...this.mergeToastOptions(options)
        };

        // Use the show method for more control
        const toast = this.toastr.show(customHtml, '', toastOptions);

        // Add click event listener to the action button
        setTimeout(() => {
            const toastEl = document.querySelector(`#${toast.toastId}`);
            if (toastEl) {
                const actionBtn = toastEl.querySelector('.toast-action-btn');
                if (actionBtn) {
                    actionBtn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        action.onClick();
                        if (!options?.disableAutoClose) {
                            this.toastr.clear(toast.toastId);
                        }
                    });
                }
            }
        }, 0);

        return toast;
    }

    // Predefined toast types with action buttons
    showSuccessWithAction(
        message: string,
        title: string = 'Success',
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        return this.showToastWithAction(message, title, ToastType.SUCCESS, action, options);
    }

    showErrorWithAction(
        message: string,        
        title: string = 'Error',
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        return this.showToastWithAction(message, title, ToastType.ERROR, action, options);
    }

    showInfoWithAction(
        message: string,
        title: string = 'Success',
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        return this.showToastWithAction(message, title, ToastType.SUCCESS, action, options);
    }

    showWarningWithAction(
        message: string,
        title: string = 'Warning',
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        return this.showToastWithAction(message, title, ToastType.WARNING, action, options);
    }

    showPrimaryWithAction(
        message: string,
        title: string = 'Notice',
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        return this.showToastWithAction(message, title, ToastType.PRIMARY, action, options);
    }    
    
    showToastFullWidth(message: string, title: string, type: ToastType, options?: any): ActiveToast<any> {
        const toastOptions = this.mergeToastOptions({
            positionClass: this.isMobileViewport() ? 'toast-top-center' : 'toast-top-full-width',
            ...options
        });

        let toast: ActiveToast<any>;

        switch (type) {
            case ToastType.SUCCESS:
                toast = this.toastr.success(message, title, toastOptions);
                break;
            case ToastType.ERROR:
                toast = this.toastr.error(message, title, toastOptions);
                break;
            case ToastType.INFO:
                toast = this.toastr.info(message, title, toastOptions);
                break;
            case ToastType.WARNING:
                toast = this.toastr.warning(message, title, toastOptions);
                break;
            case ToastType.PRIMARY:
                toast = this.toastr.show(message, title, {
                    ...toastOptions,
                    toastClass: 'ngx-toastr toast-primary'
                });
                break;
            default:
                toast = this.toastr.info(message, title, toastOptions);
        }

        return toast;
    }
    showErrorWithActionfullwidth(
        message: string,
        title: string = 'Error',
        action: ToastAction,
        options?: any
    ): ActiveToast<any> {
        return this.showToastFullWidth(message, title, ToastType.ERROR, action);
    }
}